import { memo } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "next-i18next";
import { Typography } from "src/components-mui/base/typography/typography";
import { useDateTimeFormatter } from "src/hooks/formatters/use-date-time-formatter";
import { useNumberFormatter } from "src/hooks/formatters/use-number-formatter";
import { EnumFigureType, IFigure } from "src/types/common";
import { getPerformanceColor } from "src/utils/color-util";

const RANKING_VALUE_TEST_ID = "ranking-value-test-id";
const RANKING_VALUE_MAX_WIDTH = "120px";

interface ITableRankingValueProps {
    ranking?: IFigure;
    fontWeight?: "normal" | "medium" | "semibold" | "bold" | "heavy";
}

export const TableRankingValue = memo(({ ranking, fontWeight = "bold" }: ITableRankingValueProps) => {
    const { t } = useTranslation("common");

    const { formatMoney, formatNumber, formatPerformance, formatPercent, formatVolatility } = useNumberFormatter();
    const { formatDateShort } = useDateTimeFormatter();

    if (!ranking) {
        return null;
    }

    const { value, type, creationDate, date } = ranking;

    let valueDisplay: string | number = "-";
    let color: string | undefined;

    if (type === EnumFigureType.Date) {
        valueDisplay = date ? formatDateShort(date) : "-";
    } else if (value !== null && value !== "") {
        const number = Number.parseFloat(String(value).replace(",", "."));

        switch (type) {
            case EnumFigureType.InvestedCapital:
                if (number === 0) {
                    valueDisplay = "0";
                } else {
                    valueDisplay = formatMoney(number, t);
                }
                break;
            case EnumFigureType.MaxLoss:
                valueDisplay = formatPerformance(number, 1);
                break;
            case EnumFigureType.Volatility:
                valueDisplay = formatVolatility(number, 1);
                break;
            case EnumFigureType.Performance:
                valueDisplay = formatPerformance(number, 1);
                color = getPerformanceColor(valueDisplay);
                break;
            case EnumFigureType.SharpRatio:
                valueDisplay = formatNumber(number, 1);
                break;
            case EnumFigureType.Reservations:
                valueDisplay = formatNumber(number);
                break;
            case EnumFigureType.LiquidationFigure:
                valueDisplay = value;
                break;
            case EnumFigureType.TradingVolume:
                valueDisplay = formatMoney(number, t);
                break;
            case EnumFigureType.Price:
                valueDisplay = formatNumber(number, 2);
                break;
            case EnumFigureType.PerformanceFee:
                valueDisplay = formatPercent(number, 1);
                break;
            case EnumFigureType.RankingPlace:
                valueDisplay = `${value}.`;
                break;
            case EnumFigureType.Isin:
                valueDisplay = value;
                break;
            default:
                valueDisplay = value;
        }
    }

    return (
        <Box
            sx={{
                color: color,
                fontWeight,
                maxWidth: RANKING_VALUE_MAX_WIDTH,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            }}
            data-test-id={RANKING_VALUE_TEST_ID}
        >
            {valueDisplay}
            {creationDate && (
                <Typography
                    component="span"
                    sx={{
                        color: "gray.500",
                        fontSize: "xs",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                    }}
                >
                    {t("table.since", {
                        0: formatDateShort(creationDate),
                    })}
                </Typography>
            )}
        </Box>
    );
});

TableRankingValue.displayName = "TableRankingValue";
